import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Breadcrumbs from "../common/breadcrumbs/breadcrumbs.component";
import { ReactComponent as BreadCrumbsArrowLeft } from "../../assets/leftArrow.svg";
import { ReactComponent as BreadCrumbsArrow } from "../../assets/rightArrow.svg";
import { ReactComponent as EnLogo } from "../../assets/logo_en.svg";
import { h1, h2, h3, h5Regular, h6Regular } from "../../shared/typography";
import ourVisionImage from "./ourVisionImage.png";
import whatWeDoImage from "./whatWeDoImage.png";
import whyLocalMattersImage from "./whyLocalMattersImage.png";

const getParagraphs = (value: unknown): string[] => {
  return Array.isArray(value) ? value.filter(Boolean).map(String) : [];
};

const useStyles = makeStyles({
  root: {
    boxSizing: "border-box",
    color: "#15222f",
    marginLeft: "calc(50% - 50vw)",
    marginRight: "calc(50% - 50vw)",
    paddingTop: 140,
    width: "100vw",
    "& *": {
      boxSizing: "border-box",
    },
    "@media screen and (max-width: 940px)": {
      marginLeft: 0,
      marginRight: 0,
      paddingTop: 168,
      width: "auto",
    },
  },
  intro: {
    boxSizing: "content-box",
    margin: "0 auto 120px",
    maxWidth: 1248,
    paddingLeft: 32,
    paddingRight: 32,
    textAlign: "center",
    width: "78%",
    "@media screen and (max-width: 940px)": {
      marginBottom: 56,
      maxWidth: "none",
      paddingLeft: 16,
      paddingRight: 16,
      width: "auto",
    },
  },
  navigation: {
    marginBottom: 56,
    textAlign: "left",
    "& .breadcrumbs-container": {
      marginTop: 0,
    },
    "@media screen and (max-width: 940px)": {
      marginBottom: 24,
    },
  },
  desktopNavigation: {
    display: "block",
    "@media screen and (max-width: 940px)": {
      display: "none",
    },
  },
  mobileNavigation: {
    alignItems: "center",
    color: "#80bb34",
    display: "none",
    gap: 8,
    justifyContent: "flex-start",
    "@media screen and (max-width: 940px)": {
      display: "flex",
    },
  },
  navigationCurrent: {
    color: "rgba(21, 34, 47, 0.64)",
  },
  logo: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 56,
    "@media screen and (max-width: 940px)": {
      marginBottom: 48,
    },
  },
  section: {
    boxSizing: "content-box",
    margin: "0 auto 120px",
    maxWidth: 1248,
    paddingLeft: 32,
    paddingRight: 32,
    width: "78%",
    "@media screen and (max-width: 940px)": {
      marginBottom: 56,
      maxWidth: "none",
      paddingLeft: 16,
      paddingRight: 16,
      textAlign: "center",
      width: "auto",
    },
  },
  missionSection: {
    "@media screen and (max-width: 940px)": {
      display: "none",
    },
  },
  sectionSplit: {
    alignItems: "center",
    display: "grid",
    gap: 80,
    gridTemplateColumns: "minmax(0, 1fr) 418px",
    "@media screen and (max-width: 940px)": {
      gap: 56,
      gridTemplateColumns: "1fr",
    },
  },
  sectionSplitReversed: {
    gridTemplateColumns: "418px minmax(0, 1fr)",
    "@media screen and (max-width: 940px)": {
      gridTemplateColumns: "1fr",
    },
  },
  sectionColumns: {
    display: "grid",
    gap: 120,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    "@media screen and (max-width: 940px)": {
      gap: 56,
      gridTemplateColumns: "1fr",
    },
  },
  finalSection: {
    marginBottom: 0,
  },
  text: {
    maxWidth: 640,
    "@media screen and (max-width: 940px)": {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "none",
      width: "100%",
    },
  },
  missionText: {
    maxWidth: "none",
    "@media screen and (max-width: 940px)": {
      maxWidth: "none",
    },
  },
  textRtl: {
    textAlign: "right",
    "@media screen and (max-width: 940px)": {
      textAlign: "right",
    },
  },
  textLtr: {
    textAlign: "left",
    "@media screen and (max-width: 940px)": {
      textAlign: "left",
    },
  },
  narrowTextRtl: {
    marginLeft: "auto",
  },
  narrowTextLtr: {
    marginRight: "auto",
  },
  heroTitle: {
    ...h1,
    margin: "0 0 40px",
    "@media screen and (max-width: 940px)": {
      ...h3,
      marginBottom: 16,
    },
  },
  title: {
    ...h2,
    margin: "0 0 40px",
    "@media screen and (max-width: 940px)": {
      ...h3,
      marginBottom: 16,
    },
  },
  copy: {
    ...h5Regular,
    margin: 0,
    opacity: 0.64,
    "& + &": {
      marginTop: 24,
    },
  },
  heroCopy: {
    ...h6Regular,
    margin: 0,
    opacity: 0.64,
    "@media screen and (max-width: 940px)": {
      ...h5Regular,
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "none",
      width: "100%",
      opacity: 0.64,
    },
  },
  placeholder: {
    background: "linear-gradient(135deg, #5f8fb3 0%, #2b5a72 100%)",
    borderRadius: 16,
    minHeight: 418,
    width: "100%",
    "@media screen and (max-width: 940px)": {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: 520,
      minHeight: 0,
    },
    "@media screen and (max-width: 480px)": {
      maxWidth: 343,
    },
  },
  image: {
    aspectRatio: "1 / 1",
    display: "block",
    height: 418,
    objectFit: "cover",
    objectPosition: "center",
    "@media screen and (max-width: 940px)": {
      height: "auto",
    },
  },
  imageFirstOnMobile: {
    "@media screen and (max-width: 940px)": {
      marginBottom: -32,
      order: -1,
    },
  },
  marketPlaceholder: {
    background: "linear-gradient(135deg, #7fae72 0%, #2f6d5f 100%)",
  },
  vision: {
    alignItems: "center",
    background:
      `linear-gradient(rgba(8, 21, 28, 0.56), rgba(8, 21, 28, 0.56)), url(${ourVisionImage})`,
    backgroundPosition: "center",
    backgroundSize: "cover",
    display: "flex",
    justifyContent: "center",
    marginBottom: 120,
    minHeight: 360,
    padding: "80px 40px",
    textAlign: "center",
    width: "100%",
    "@media screen and (max-width: 940px)": {
      marginBottom: 56,
      minHeight: 638,
      padding: "80px 24px",
    },
  },
  visionContent: {
    maxWidth: 760,
  },
  visionTitle: {
    color: "#ffffff",
    "@media screen and (max-width: 940px)": {
      ...h2,
      color: "#ffffff",
      marginBottom: 32,
    },
  },
  visionCopy: {
    color: "#ffffff",
    opacity: 1,
  },
  rtlTypography: {
    fontFamily: "Assistant, Barlow, sans-serif",
    fontWeight: 700,
  },
  rtlCopy: {
    fontFamily: "Assistant, Barlow, sans-serif",
  },
});

export const OurStory: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dir = t("css.side") as "ltr" | "rtl";
  const isRtl = dir === "rtl";
  const classes = useStyles();
  const sharonFarmsLabel = t("footer.sharonFarms");
  const ourStoryLabel = t("footer.ourStory");
  const onBackToHome = () => history.push("/");

  const whatWeDoParagraphs = getParagraphs(
    t("ourStory.sections.whatWeDo.paragraphs", { returnObjects: true })
  );
  const localMattersParagraphs = getParagraphs(
    t("ourStory.sections.localMatters.paragraphs", { returnObjects: true })
  );
  const merchantsParagraphs = getParagraphs(
    t("ourStory.sections.merchants.paragraphs", { returnObjects: true })
  );
  const licenseesParagraphs = getParagraphs(
    t("ourStory.sections.licensees.paragraphs", { returnObjects: true })
  );

  return (
    <div className={classes.root} dir={dir}>
      <section className={classes.intro}>
        <nav className={classes.navigation} aria-label={ourStoryLabel}>
          <div className={classes.desktopNavigation}>
            <Breadcrumbs onGoBack={onBackToHome} prevPage={sharonFarmsLabel}>
              <BreadCrumbsArrow style={{ margin: "0 16px" }} />
              <span className={`h6-regular ${classes.navigationCurrent}`}>
                {ourStoryLabel}
              </span>
            </Breadcrumbs>
          </div>
          <div
            className={`h5-medium pointer ${classes.mobileNavigation}`}
            onClick={onBackToHome}
          >
            <BreadCrumbsArrowLeft style={{ width: 24, height: 24 }} />
            {sharonFarmsLabel}
          </div>
        </nav>
        <div className={classes.logo} aria-hidden="true">
          <EnLogo width={150} height={136} />
        </div>

        <h1
          className={`${classes.heroTitle} ${isRtl ? classes.rtlTypography : ""
            }`}
        >
          {t("ourStory.sections.hero.title")}
        </h1>
        <p className={`${classes.heroCopy} ${isRtl ? classes.rtlCopy : ""}`}>
          {t("ourStory.sections.hero.paragraph")}
        </p>
      </section>

      <section className={`${classes.section} ${classes.missionSection}`}>
        <div
          className={`${classes.text} ${classes.missionText} ${isRtl ? classes.textRtl : classes.textLtr
            }`}
        >
          <h2
            className={`${classes.title} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.mission.title")}
          </h2>
          <p className={`${classes.copy} ${isRtl ? classes.rtlCopy : ""}`}>
            {t("ourStory.sections.mission.paragraph")}
          </p>
        </div>
      </section>

      <section className={`${classes.section} ${classes.sectionSplit}`}>
        <div
          className={`${classes.text} ${isRtl ? classes.textRtl : classes.textLtr
            }`}
        >
          <h2
            className={`${classes.title} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.whatWeDo.title")}
          </h2>
          {whatWeDoParagraphs.map((paragraph) => (
            <p
              className={`${classes.copy} ${isRtl ? classes.rtlCopy : ""}`}
              key={paragraph}
            >
              {paragraph}
            </p>
          ))}
        </div>
        <img
          alt={t("ourStory.imagePlaceholder")}
          className={`${classes.placeholder} ${classes.image} ${classes.imageFirstOnMobile}`}
          src={whatWeDoImage}
        />
      </section>

      <section
        className={`${classes.section} ${classes.sectionSplit} ${classes.sectionSplitReversed}`}
      >
        <img
          alt={t("ourStory.imagePlaceholder")}
          className={`${classes.placeholder} ${classes.image} ${classes.imageFirstOnMobile} ${classes.marketPlaceholder}`}
          src={whyLocalMattersImage}
        />
        <div
          className={`${classes.text} ${isRtl ? classes.textRtl : classes.textLtr
            }`}
        >
          <h2
            className={`${classes.title} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.localMatters.title")}
          </h2>
          {localMattersParagraphs.map((paragraph) => (
            <p
              className={`${classes.copy} ${isRtl ? classes.rtlCopy : ""}`}
              key={paragraph}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      <section className={classes.vision}>
        <div
          className={`${classes.visionContent} ${isRtl ? classes.textRtl : classes.textLtr
            }`}
        >
          <h2
            className={`${classes.title} ${classes.visionTitle} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.vision.title")}
          </h2>
          <p
            className={`${classes.copy} ${classes.visionCopy} ${isRtl ? classes.rtlCopy : ""
              }`}
          >
            {t("ourStory.sections.vision.paragraph")}
          </p>
        </div>
      </section>

      <section
        className={`${classes.section} ${classes.sectionColumns} ${classes.finalSection} ${isRtl ? classes.textRtl : classes.textLtr
          }`}
      >
        <div>
          <h2
            className={`${classes.title} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.merchants.title")}
          </h2>
          {merchantsParagraphs.map((paragraph) => (
            <p
              className={`${classes.copy} ${isRtl ? classes.rtlCopy : ""}`}
              key={paragraph}
            >
              {paragraph}
            </p>
          ))}
        </div>

        <div>
          <h2
            className={`${classes.title} ${isRtl ? classes.rtlTypography : ""
              }`}
          >
            {t("ourStory.sections.licensees.title")}
          </h2>
          {licenseesParagraphs.map((paragraph) => (
            <p
              className={`${classes.copy} ${isRtl ? classes.rtlCopy : ""}`}
              key={paragraph}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
};
